/**
 * @file DynamicFormField.tsx
 * @description Shared dynamic form field renderer for all workspace entity forms.
 * Handles every FormFieldType defined in formBuilder types with proper UI components.
 * Used by WorkspaceHospitalCreatePage, WorkspaceHcpCreatePage, WorkspacePatientCreatePage.
 */

import { useRef } from 'react';
import { AlertTriangle, Upload, X, Plus, Copy, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '@/modules/shared/components/ui/input';
import { Textarea } from '@/modules/shared/components/ui/textarea';
import { Label } from '@/modules/shared/components/ui/label';
import { Checkbox } from '@/modules/shared/components/ui/checkbox';
import { Button } from '@/modules/shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/shared/components/ui/select';
import { MultiSelect } from '@/modules/shared/components/ui/multi-select';
import type { FormFieldDefinition, FormFieldOption } from '../../types/formBuilder';
import {
  getSystemGeneratedFieldPlaceholder,
  isSystemGeneratedField,
  resolveEntitySelectorConfig,
  type FieldValue,
  type UploadedFileValue,
} from './dynamicFormFieldUtils';
import {
  AutoPopulateField,
  HeaderField,
  DisplayField,
  RichTextField,
  ImageUploadField,
  ComputedField,
} from './IntelligentFieldRenderers';
import { EntitySelectorField } from './EntitySelectorField';

const getInputValue = (value: FieldValue): string | number | readonly string[] | undefined => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }
  if (Array.isArray(value) && value.every((v): v is string => typeof v === 'string')) {
    return value;
  }

  return undefined;
};

const getSelectValue = (value: FieldValue): string => (typeof value === 'string' ? value : '');

const getRowsValue = (value: string | number | boolean | null | undefined, fallback: number): number =>
  typeof value === 'number' ? value : fallback;

const isUploadedFileValue = (value: FieldValue): value is UploadedFileValue => {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'name' in value;
};

// ─────────────────────────────────────────────────────────────
// Helper: get active, sorted options from a field definition
// ─────────────────────────────────────────────────────────────

function FieldErrorMessage({ error }: { error?: string }) {
  if (!error) return null;
  return <p className="mt-0.5 text-[11px] text-destructive">{error}</p>;
}

function FieldHelpText({ text }: { text?: string }) {
  if (!text) return null;
  return <p className="text-[11px] text-muted-foreground">{text}</p>;
}

function FieldLabelRow({
  htmlFor,
  label,
  mandatory,
  className = 'text-xs font-medium',
}: {
  htmlFor?: string;
  label: string;
  mandatory?: boolean;
  className?: string;
}) {
  return (
    <Label htmlFor={htmlFor} className={className}>
      {label}
      {mandatory && <span className="text-destructive ml-1">*</span>}
    </Label>
  );
}

function getActiveOptions(options?: FormFieldOption[]) {
  if (!options?.length) return [];
  return [...options]
    .filter((o) => o.enabled !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

// ─────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────

export interface DynamicFormFieldProps {
  field: FormFieldDefinition;
  value: FieldValue;
  onChange: (fieldName: string, value: FieldValue) => void;
  error?: string;
  disabled?: boolean;
  /** Pre-computed value for COMPUTED field type */
  computedValue?: FieldValue;
  /** All form values — needed for API-driven fields */
  formValues?: Record<string, FieldValue>;
  /** Optional map from fieldId -> internalName for dependency lookup compatibility */
  dependencyFieldKeyMap?: Record<string, string>;
  /** Wrapper ref used by runtime forms for error focus/scroll. */
  fieldRef?: (node: HTMLDivElement | null) => void;
  /** Override programId for entity selectors (when no route param available) */
  programId?: string;
  /** When set, entity selectors use public API with program code (self-registration) */
  publicProgramCode?: string;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export function DynamicFormField({
  field,
  value,
  onChange,
  error,
  disabled = false,
  computedValue,
  formValues: _formValues,
  dependencyFieldKeyMap,
  fieldRef,
  programId,
  publicProgramCode,
}: DynamicFormFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const opts = getActiveOptions(field.options);
  const isReadOnly = field.properties?.readOnly === true || field.properties?.readonly === true;
  const isGenerated = isSystemGeneratedField(field);
  const isDisabled = disabled || isReadOnly || isGenerated;

  const set = (val: FieldValue) => onChange(field.internalName, val);

  const errorClass = 'border-destructive ring-1 ring-destructive/20';
  const inputClass = `h-8 text-xs ${error ? errorClass : ''}`;

  // Date constraints from validation rules
  const today = new Date().toISOString().split('T')[0];
  const minDateRule = field.validationRules?.find((r) => r.type === 'MIN_DATE');
  const maxDateRule = field.validationRules?.find((r) => r.type === 'MAX_DATE');
  const pastOnly = field.validationRules?.find((r) => r.type === 'PAST_DATES_ONLY');
  const futureOnly = field.validationRules?.find((r) => r.type === 'FUTURE_DATES_ONLY');
  const minDate = futureOnly ? today : (minDateRule?.value || '');
  const maxDate = pastOnly ? today : (maxDateRule?.value || '');

  // ── Column-span class in 12-column grid (mobile always full-width) ──
  const COLSPAN_MAP: Record<string, string> = {
    '50%': 'col-span-12 sm:col-span-6',
    '33%': 'col-span-12 sm:col-span-4',
    '20%': 'col-span-12 sm:col-span-2',
  };
  const colSpanClass = COLSPAN_MAP[field.gridWidth ?? ''] ?? 'col-span-12';

  // ── Render the input control ──────────────────────────────
  const renderControl = () => {
    switch (field.fieldType) {
      // ── Text-like inputs ───────────────────────────────────
      case 'TEXT':
      case 'USERNAME':
        return (
          <Input
            type="text"
            value={getInputValue(value)}
            onChange={(e) => set(e.target.value)}
            placeholder={getSystemGeneratedFieldPlaceholder(field)}
            disabled={isDisabled}
            className={inputClass}
          />
        );

      case 'AUTO_SEQUENCE':
      case 'AUTO_GENERATED':
      case 'SYSTEM_FIELD':
        return (
          <Input
            type="text"
            value={getInputValue(value)}
            placeholder="Auto-generated on save"
            disabled
            readOnly
            className={inputClass}
          />
        );

      case 'LOOKUP_READ_ONLY':
        return (
          <Input
            type="text"
            value={getInputValue(value)}
            placeholder="Auto-filled from linked record"
            disabled
            readOnly
            className={inputClass}
          />
        );

      case 'READ_ONLY':
        return (
          <Input
            type="text"
            value={getInputValue(value)}
            placeholder={field.placeholder || 'Not set'}
            disabled
            readOnly
            className={inputClass}
          />
        );

      case 'CURRENCY':
        return (
          <Input
            type="number"
            step="0.01"
            value={getInputValue(value)}
            onChange={(e) => set(e.target.value !== '' ? Number(e.target.value) : '')}
            placeholder={field.placeholder || '0.00'}
            disabled={isDisabled}
            className={inputClass}
          />
        );

      case 'LOOKUP':
        // A read-only LOOKUP (derived display, e.g. "Hospital Name" once a Hospital is picked
        // elsewhere) renders as plain disabled text; otherwise it's the actual relationship
        // picker and needs the same live-search UI as ENTITY_SELECTOR.
        if (isGenerated) {
          return (
            <Input
              type="text"
              value={getInputValue(value)}
              placeholder="Auto-filled from linked record"
              disabled
              readOnly
              className={inputClass}
            />
          );
        }
        return (
          <EntitySelectorField
            field={{ ...field, entitySelectorConfig: resolveEntitySelectorConfig(field) }}
            value={value as string | number | boolean | string[] | Record<string, unknown>[] | null | undefined}
            onChange={onChange}
            error={error}
            disabled={isDisabled}
            formValues={_formValues as Record<string, string | number | boolean | string[] | Record<string, unknown>[] | null | undefined>}
            dependencyFieldKeyMap={dependencyFieldKeyMap}
            programId={programId}
            publicProgramCode={publicProgramCode}
          />
        );

      case 'EMAIL':
        return (
          <Input
            type="email"
            value={getInputValue(value)}
            onChange={(e) => set(e.target.value)}
            placeholder={field.placeholder || ''}
            disabled={isDisabled}
            className={inputClass}
          />
        );

      case 'PHONE':
        return (
          <Input
            type="tel"
            value={getInputValue(value)}
            onChange={(e) => set(e.target.value)}
            placeholder={field.placeholder || ''}
            disabled={isDisabled}
            className={inputClass}
          />
        );

      case 'NUMBER':
        return (
          <Input
            type="number"
            value={getInputValue(value)}
            onChange={(e) => set(e.target.value !== '' ? Number(e.target.value) : '')}
            placeholder={field.placeholder || ''}
            disabled={isDisabled}
            className={inputClass}
          />
        );

      // ── Textarea ───────────────────────────────────────────
      case 'TEXTAREA':
        return (
          <Textarea
            value={getInputValue(value)}
            onChange={(e) => set(e.target.value)}
            placeholder={field.placeholder || ''}
            rows={getRowsValue(field.properties?.rows, 3)}
            disabled={isDisabled}
            className={`text-xs resize-none ${error ? errorClass : ''}`}
          />
        );

      // ── Date ───────────────────────────────────────────────
      case 'DATE':
        return (
          <Input
            type="date"
            value={getInputValue(value)}
            onChange={(e) => set(e.target.value)}
            min={minDate}
            max={maxDate}
            disabled={isDisabled}
            className={inputClass}
          />
        );

      // ── Dropdown / Select ──────────────────────────────────
      case 'DROPDOWN':
        return (
          <Select
            value={getSelectValue(value)}
            onValueChange={(nextValue) => set(nextValue)}
            disabled={isDisabled}
          >
            <SelectTrigger className={`h-8 text-xs ${error ? errorClass : ''}`}>
              <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {opts.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      // ── Radio ──────────────────────────────────────────────
      case 'RADIO':
        return (
          <div className="flex flex-col gap-2 pt-0.5">
            {opts.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center gap-2 cursor-pointer ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <input
                  type="radio"
                  name={field.fieldId}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={() => set(opt.value)}
                  disabled={isDisabled}
                  className="h-3.5 w-3.5 accent-primary"
                />
                <span className="text-xs">{opt.label}</span>
              </label>
            ))}
            {opts.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic">No options configured</p>
            )}
          </div>
        );

      // ── Multi-select ───────────────────────────────────────
      case 'MULTISELECT': {
        const multiOpts = opts.map((o) => ({ value: o.value, label: o.label }));
        const selected: string[] = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
        return (
          <MultiSelect
            options={multiOpts}
            value={selected}
            onChange={set}
            placeholder={field.placeholder || `Select ${field.label}`}
            disabled={isDisabled}
            error={!!error}
          />
        );
      }

      // ── Checkbox ───────────────────────────────────────────
      case 'CHECKBOX':
        return (
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id={field.fieldId}
              checked={!!value}
              onCheckedChange={(checked) => set(checked === true)}
              disabled={isDisabled}
            />
            <Label htmlFor={field.fieldId} className="text-xs font-normal cursor-pointer">
              {field.placeholder || field.label}
            </Label>
          </div>
        );

      // ── File upload ────────────────────────────────────────
      case 'FILE': {
        const fileName = value
          ? isUploadedFileValue(value)
            ? value.name
            : typeof value === 'string'
            ? value
            : 'File selected'
          : null;

        const acceptedTypes = field.validationRules
          ?.filter((r) => r.type === 'FILE_TYPE')
          .map((r) => r.value)
          .join(',');

        const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0] ?? null;
          if (file) {
            // Store filename + convert to base64 for form submission
            const reader = new FileReader();
            reader.onload = () => {
              set({ name: file.name, size: file.size, type: file.type, data: reader.result });
            };
            reader.readAsDataURL(file);
          } else {
            set(null);
          }
        };

        return (
          <div
            className={`relative border-2 border-dashed rounded-md text-center cursor-pointer transition-colors ${
              error ? errorClass : 'border-border hover:border-primary/50'
            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !isDisabled && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedTypes}
              onChange={handleFileChange}
              disabled={isDisabled}
              className="hidden"
            />
            <div className="py-3 px-4">
              {fileName ? (
                <div className="flex items-center justify-between">
                  <span className="text-xs truncate max-w-[calc(100%-2rem)]">{fileName}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); set(null); }}
                    className="ml-2 text-muted-foreground hover:text-destructive"
                    disabled={isDisabled}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Upload className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-[11px] text-muted-foreground">
                    {field.placeholder || 'Click to upload a file'}
                  </span>
                  {acceptedTypes && (
                    <span className="text-[11px] text-muted-foreground/70">
                      Accepted: {acceptedTypes}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }

      // ── Terms & Conditions ─────────────────────────────────
      case 'TERMS_AND_CONDITIONS':
        return (
          <div className="space-y-2">
            {field.properties?.content && (
              <div className="max-h-40 overflow-y-auto rounded border bg-muted/30 p-3 text-[11px] text-muted-foreground">
                <div dangerouslySetInnerHTML={{ __html: field.properties.content }} />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${field.fieldId}_consent`}
                checked={!!value}
                onCheckedChange={(checked) => set(checked === true)}
                disabled={isDisabled}
              />
              <Label htmlFor={`${field.fieldId}_consent`} className="text-xs font-normal cursor-pointer">
                {field.properties?.consentText || 'I agree to the Terms & Conditions'}
                {field.mandatory && <span className="text-destructive ml-1">*</span>}
              </Label>
            </div>
          </div>
        );

      // ── Paragraph (display-only rich content) ─────────────
      case 'PARAGRAPH':
        return (
          <div className="rounded border bg-muted/20 p-3">
            {field.properties?.content ? (
              <div
                className={`prose prose-sm max-w-none text-foreground ${
                  field.properties?.size === 'sm' ? 'text-[11px]' :
                  field.properties?.size === 'lg' ? 'text-sm' :
                  'text-xs'
                } ${
                  field.properties?.align === 'center' ? 'text-center' :
                  field.properties?.align === 'right' ? 'text-right' :
                  'text-left'
                }`}
                dangerouslySetInnerHTML={{ __html: String(field.properties.content) }}
              />
            ) : (
              <p className="text-[11px] text-muted-foreground italic">No content configured</p>
            )}
          </div>
        );

      // ── Password ───────────────────────────────────────────
      case 'PASSWORD':
        return (
          <div className="space-y-1.5">
            <Input
              type="password"
              value={getInputValue(value)}
              onChange={(e) => set(e.target.value)}
              placeholder={field.placeholder || 'Enter password'}
              disabled={isDisabled}
              className={inputClass}
            />
            {field.properties?.showConfirm && (
              <Input
                type="password"
                placeholder="Confirm password"
                disabled={isDisabled}
                className={inputClass}
              />
            )}
          </div>
        );

      // ── Field Group (structural container) ─────────────────
      case 'FIELD_GROUP':
        return (
          <div className="rounded-lg border border-border p-3 space-y-2 bg-muted/10">
            {field.helpText && (
              <p className="text-[11px] text-muted-foreground">{field.helpText}</p>
            )}
            {/* Child fields are rendered by the parent page (DynamicFormPage) */}
          </div>
        );

      // ── Repeatable Group (dynamic array) ───────────────────
      case 'REPEATABLE_GROUP': {
        const items = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
        const minItems = typeof field.properties?.minItems === 'number' ? field.properties.minItems : 0;
        const maxItems = typeof field.properties?.maxItems === 'number' ? field.properties.maxItems : 50;
        const canAdd = items.length < maxItems;
        const canRemove = items.length > minItems;

        // Resolve child fields from childFieldIds
        const childIds = typeof field.properties?.childFieldIds === 'string'
          ? (field.properties.childFieldIds as string).split(',').map((s) => s.trim()).filter(Boolean)
          : [];

        const addItem = () => {
          if (!canAdd) return;
          set([...items, {}] as FieldValue);
        };

        const duplicateItem = (index: number) => {
          if (!canAdd) return;
          const clone = JSON.parse(JSON.stringify(items[index] ?? {}));
          const next = [...items];
          next.splice(index + 1, 0, clone);
          set(next as FieldValue);
        };

        const removeItem = (index: number) => {
          if (!canRemove) return;
          set(items.filter((_, i) => i !== index) as FieldValue);
        };

        const moveItem = (index: number, direction: -1 | 1) => {
          const target = index + direction;
          if (target < 0 || target >= items.length) return;
          const next = [...items];
          [next[index], next[target]] = [next[target], next[index]];
          set(next as FieldValue);
        };

        return (
          <div className="rounded-lg border border-border p-3 space-y-2 bg-muted/10">
            {field.helpText && (
              <p className="text-[11px] text-muted-foreground">{field.helpText}</p>
            )}

            {items.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic py-2 text-center">
                No items yet. Click &quot;Add Item&quot; to begin.
              </p>
            )}

            {items.map((_item, index) => (
              <div key={index} className="rounded border border-border/50 p-2 bg-background space-y-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-medium text-muted-foreground">
                    Item {index + 1}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0 || disabled}
                      className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(index, 1)}
                      disabled={index === items.length - 1 || disabled}
                      className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => duplicateItem(index)}
                      disabled={!canAdd || disabled}
                      className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                      title="Duplicate item"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      disabled={!canRemove || disabled}
                      className="p-0.5 rounded hover:bg-destructive-soft hover:text-destructive disabled:opacity-30"
                      title="Remove item"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                {/* Row child fields are rendered by DynamicFormPage which passes children context */}
                {childIds.length > 0 ? (
                  <p className="text-[11px] text-muted-foreground">
                    {childIds.length} field{childIds.length !== 1 ? 's' : ''} per row
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground italic">
                    No child fields configured
                  </p>
                )}
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
              disabled={!canAdd || disabled}
              className="h-6 text-[11px] gap-1 w-full"
            >
              <Plus className="w-3 h-3" /> Add Item
            </Button>

            <p className="text-[11px] text-muted-foreground">
              {items.length} item{items.length !== 1 ? 's' : ''}
              {maxItems < 50 ? ` (max ${maxItems})` : ''}
            </p>
          </div>
        );
      }

      // ── Intelligent components ─────────────────────────────
      case 'AUTOPOPULATE':
        return <AutoPopulateField field={field} onChange={onChange} disabled={isDisabled} />;

      case 'HEADER':
        return <HeaderField field={field} />;

      case 'DISPLAY':
        return <DisplayField field={field} />;

      case 'RICHTEXT':
        return <RichTextField field={field} value={value} onChange={onChange} error={error} disabled={isDisabled} />;

      case 'IMAGE':
        return <ImageUploadField field={field} value={value} onChange={onChange} error={error} disabled={isDisabled} />;

      case 'COMPUTED':
        return <ComputedField field={field} computedValue={computedValue} />;

      case 'ENTITY_SELECTOR':
      case 'DEPENDENT_DROPDOWN':
        return (
          <EntitySelectorField
            field={{ ...field, entitySelectorConfig: resolveEntitySelectorConfig(field) }}
            value={value as string | number | boolean | string[] | Record<string, unknown>[] | null | undefined}
            onChange={onChange}
            error={error}
            disabled={isDisabled}
            formValues={_formValues as Record<string, string | number | boolean | string[] | Record<string, unknown>[] | null | undefined>}
            dependencyFieldKeyMap={dependencyFieldKeyMap}
            programId={programId}
            publicProgramCode={publicProgramCode}
          />
        );

      // ── Unknown type — show warning instead of silent fallback ──
      default: {
        const unknownType = field.fieldType ?? 'UNKNOWN';
        console.warn(`[DynamicFormField] Unregistered field type: "${unknownType}" for field "${field.internalName}"`);
        return (
          <div className="flex items-center gap-1.5 rounded border border-warning/20 bg-warning-soft px-2 py-1.5 text-[11px] text-warning">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span>Unsupported field type: <strong>{unknownType}</strong></span>
          </div>
        );
      }
    }
  };

  // ── CHECKBOX: render without outer label ──────────────────
  if (field.fieldType === 'CHECKBOX') {
    return (
      <div ref={fieldRef} className={colSpanClass} data-field-key={field.internalName} tabIndex={-1}>
        {renderControl()}
        {field.helpText && (
          <p className="mt-1 text-[11px] text-muted-foreground">{field.helpText}</p>
        )}
        <FieldErrorMessage error={error} />
      </div>
    );
  }

  // ── PARAGRAPH: display-only, no label/validation ──────────
  if (field.fieldType === 'PARAGRAPH') {
    return (
      <div ref={fieldRef} className={colSpanClass} data-field-key={field.internalName} tabIndex={-1}>
        {renderControl()}
      </div>
    );
  }

  // ── HEADER / DISPLAY / AUTOPOPULATE / IMAGE / RICHTEXT / COMPUTED: self-contained ─
  if (['HEADER', 'DISPLAY', 'AUTOPOPULATE', 'IMAGE', 'RICHTEXT', 'COMPUTED'].includes(field.fieldType)) {
    return (
      <div ref={fieldRef} className={colSpanClass} data-field-key={field.internalName} tabIndex={-1}>
        {renderControl()}
      </div>
    );
  }

  // ── FIELD_GROUP / REPEATABLE_GROUP: section header style ──
  if (field.fieldType === 'FIELD_GROUP' || field.fieldType === 'REPEATABLE_GROUP') {
    return (
      <div ref={fieldRef} className={`${colSpanClass} space-y-1`} data-field-key={field.internalName} tabIndex={-1}>
        <Label className="text-xs font-semibold">{field.label}</Label>
        {renderControl()}
      </div>
    );
  }

  // ── TERMS_AND_CONDITIONS: no separate label needed ────────
  if (field.fieldType === 'TERMS_AND_CONDITIONS') {
    return (
      <div ref={fieldRef} className={`${colSpanClass} space-y-1`} data-field-key={field.internalName} tabIndex={-1}>
        <FieldLabelRow label={field.label} mandatory={field.mandatory} />
        <FieldHelpText text={field.helpText} />
        {renderControl()}
        <FieldErrorMessage error={error} />
      </div>
    );
  }

  // ── Standard wrapper ──────────────────────────────────────
  return (
    <div ref={fieldRef} className={`space-y-1 ${colSpanClass}`} data-field-key={field.internalName} tabIndex={-1}>
      <FieldLabelRow htmlFor={field.fieldId} label={field.label} mandatory={field.mandatory} />
      <FieldHelpText text={field.helpText} />
      {renderControl()}
      <FieldErrorMessage error={error} />
    </div>
  );
}

